/**
 * GeoJSON->SVG Converter
 *
 * DEV NOTE: Just use the `useGeoJson2Svg()` hook instead, don't call these methods directly.
 *
 * Converts GeoJSON to an SVG, rendered on top of a tild map background.
 *
 * Supported GeoJSON:
 * - Polygon
 * - MultiPolygon
 */
import GeoJSON, { Feature } from 'geojson';
import * as turf from '@turf/turf';
import axios from 'axios';
import { G, SVG, Svg } from '@svgdotjs/svg.js';
import { formatMonth } from './date';

type GeoJson2SvgPolygonFeatureProperties = {
  fillColor?: string;
  strokeColor?: string;
  fillOpacity?: number;
  strokeOpacity?: number;
  strokeWidth?: number;
};
export type GeoJson2SvgPolygonFeature<
  T extends
    GeoJson2SvgPolygonFeatureProperties = GeoJson2SvgPolygonFeatureProperties,
> =
  | GeoJSON.Polygon
  | GeoJSON.MultiPolygon
  | Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, T>;
type DrawPath = {
  coordinates: GeoJSON.Position[][];
  fillColor: string;
  strokeColor: string;
  fillOpacity: number;
  strokeOpacity: number;
  strokeWidth: number;
};

// const DEFAULT_CRS = 'EPSG:4326';
// const DESIRED_CRS = 'EPSG:3857'; // AKA GOOGLE
export const TILE_SIZE = 256;
const DEFAULT_FILL_COLOR = 'rgb(155,155,155)';
const DEFAULT_FILL_OPACITY = 0.1;
const DEFAULT_STROKE_COLOR = 'rgb(0,0,0)';
const DEFAULT_STROKE_OPACITY = 0.1;
const DEFAULT_STROKE_WIDTH = 1;
const LABEL_STYLES = {
  padding: [3, 3],
};

const rad2deg = (x: number) => x * (180 / Math.PI);

// const moveRing = (
//   xy: GeoJSON.Position[],
//   [dx, dy]: GeoJSON.Position
// ): GeoJSON.Position[] => xy.map(([x, y]) => [x + dx, y + dy]);

// const moveRings = (
//   xy: GeoJSON.Position[][],
//   offset: GeoJSON.Position
// ): GeoJSON.Position[][] => xy.map((ring) => moveRing(ring, offset));

const negateCoord = (xy: GeoJSON.Position): GeoJSON.Position => [
  -xy[0],
  -xy[1],
];

const moveCoord = (
  xy: GeoJSON.Position,
  [dx, dy]: GeoJSON.Position
): GeoJSON.Position => [xy[0] + dx, xy[1] + dy];

const xyToPathString = (xy: GeoJSON.Position[]): string => {
  const [[x0, y0], ...rest] = xy;
  return [`M${x0} ${y0}`, ...rest.map(([x, y]) => `L${x} ${y}`)].join(' ');
};

// const getBboxDimensions = (bbox: GeoJSON.BBox): [number, number] => {
//   const [xMin, yMin, xMax, yMax] = bbox;
//   return [xMax - xMin, yMax - yMin];
// };

const getBboxCorners = (bbox: GeoJSON.BBox) => ({
  sw: [bbox[0], bbox[1]],
  ne: [bbox[2], bbox[3]],
});

/**
 * Get minimum bounding box that encompasses all bounding boxes.
 */
const combineBboxes = (bboxes: GeoJSON.BBox[]) => {
  if (bboxes.length === 0) return null;
  if (bboxes.length === 1) return bboxes[0];
  return bboxes.slice(1).reduce((acc, bbox): GeoJSON.BBox => {
    return [
      Math.min(acc[0], bbox[0]),
      Math.min(acc[1], bbox[1]),
      Math.max(acc[2], bbox[2]),
      Math.max(acc[3], bbox[3]),
    ];
  }, bboxes[0]);
};

/**
 * Adds a buffer to a bounding box. Buffer in meters.
 */
const addBufferToBbox = (bbox: GeoJSON.BBox, buffer: number): GeoJSON.BBox => {
  const { sw, ne } = getBboxCorners(bbox);
  const swB = turf.destination(sw, Math.SQRT2 * buffer, -135, {
    units: 'meters',
  }).geometry.coordinates;
  const neB = turf.destination(ne, Math.SQRT2 * buffer, 45, { units: 'meters' })
    .geometry.coordinates;
  return [swB[0], swB[1], neB[0], neB[1]];
};

const getViewportFromBbox = (bbox: GeoJSON.BBox, scale: number) => {
  function toPixels(latLng: GeoJSON.Position) {
    const worldCoord = project(latLng);
    return [
      Math.floor(worldCoord[0] * scale),
      Math.floor(worldCoord[1] * scale),
    ];
  }

  const { sw, ne } = getBboxCorners(bbox);
  const swPx = toPixels(sw);
  const nePx = toPixels(ne);
  return {
    width: Math.abs(nePx[0] - swPx[0]),
    height: Math.abs(nePx[1] - swPx[1]),
  };
};

/**
 * Calculate the zoom level for a given bounding box.
 * Credit: https://stackoverflow.com/a/13274361
 */
const getZoomLevel = (
  bbox: GeoJSON.BBox,
  pixelDim: { width: number; height: number }
) => {
  const WORLD_DIM = { width: 256, height: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const sw = { lng: bbox[0], lat: bbox[1] };
  const ne = { lng: bbox[2], lat: bbox[3] };

  // if (sw.lng > ne.lng || sw.lat > ne.lat) {
  //   throw new Error('invalid bbox');
  // }

  const latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;
  const lngDiff = ne.lng - sw.lng;
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(pixelDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(pixelDim.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
};

// The mapping between latitude, longitude and pixels is defined by the web
// mercator projection.
//  source: https://developers.google.com/maps/documentation/javascript/examples/map-coordinates
function project(latLng: GeoJSON.Position): GeoJSON.Position {
  let sinY = Math.sin((latLng[1] * Math.PI) / 180);

  // Truncating to 0.9999 effectively limits latitude to 89.189. This is
  // about a third of a tile past the edge of the world tile.
  sinY = Math.min(Math.max(sinY, -0.9999), 0.9999);

  return [
    TILE_SIZE * (0.5 + latLng[0] / 360),
    TILE_SIZE * (0.5 - Math.log((1 + sinY) / (1 - sinY)) / (4 * Math.PI)),
  ];
}

const latLngToTileXy = (latLng: GeoJSON.Position, scale: number) => {
  const worldCoord = project(latLng);
  const pixelCoord = [
    // it might be technically more accurate to remove Math.floor here but the difference is negligible
    Math.floor(worldCoord[0] * scale),
    Math.floor(worldCoord[1] * scale),
  ];
  const x = Math.floor(pixelCoord[0] / TILE_SIZE);
  const y = Math.floor(pixelCoord[1] / TILE_SIZE);

  return { x, y };
};

const tileXyToLatLng = (tile: { x: number; y: number }, scale: number) => {
  function invProject(worldCoord: GeoJSON.Position) {
    const latRad =
      2 * Math.atan(Math.exp(2 * Math.PI * (0.5 - worldCoord[1] / TILE_SIZE))) -
      Math.PI / 2;
    return [360.0 * (worldCoord[0] / TILE_SIZE - 0.5), rad2deg(latRad)];
  }

  const pixelCoord = [tile.x * TILE_SIZE, tile.y * TILE_SIZE];
  const worldCoord = [pixelCoord[0] / scale, pixelCoord[1] / scale];
  return invProject(worldCoord);
};

const zoomToScale = (zoom: number) => Math.pow(2, zoom);

const getAerometrexTileImage = async (
  tile: { x: number; y: number },
  zoom: number
) => {
  const resp = await axios.get(
    `${process.env.AEROMETREX_TILES_URL}/${process.env.AEROMETREX_TILES_KEY}/service?SERVICE=WMTS&REQUEST=GetTile&LAYER=Australia_latest&FORMAT=png&VERSION=1.0.0&STYLE=default&TileMatrixSet=webmercator&TileMatrix=${zoom}&TileRow=${tile.y}&TileCol=${tile.x}`,
    {
      responseType: 'blob',
      headers: { 'Access-Control-Allow-Origin': '*' },
    }
  );
  return resp.data;
};

function cleanPolygon<T extends GeoJson2SvgPolygonFeatureProperties>(
  feat: GeoJson2SvgPolygonFeature<T>
) {
  const { geometry, properties } =
    feat.type === 'Feature' ? feat : { geometry: feat };
  // make outer rings CCW and inner rings CW
  const cleaned = turf.rewind(geometry);
  if (cleaned.type === 'MultiPolygon') {
    return turf.multiPolygon(cleaned.coordinates, properties, {
      bbox: feat.bbox,
    });
  } else if (cleaned.type === 'Polygon') {
    return turf.polygon(cleaned.coordinates, properties, {
      bbox: feat.bbox,
    });
  } else {
    throw new Error('Expected Polygon or MultiPolygon');
  }
}

function withBbox<T extends GeoJson2SvgPolygonFeatureProperties>(
  feat: GeoJson2SvgPolygonFeature<T>
) {
  const { geometry, properties } =
    feat.type === 'Feature' ? feat : { geometry: feat };
  const bbox = turf.bbox(geometry);
  if (geometry.type === 'MultiPolygon') {
    return turf.multiPolygon(geometry.coordinates, properties, { bbox });
  } else {
    return turf.polygon(geometry.coordinates, properties, { bbox });
  }
}

/**
 * Setup script. Web and worker friendly.
 */
export async function geoJson2SvgSetup<
  T extends GeoJson2SvgPolygonFeatureProperties,
>(
  inputFeats:
    | GeoJson2SvgPolygonFeature<T>[]
    | GeoJSON.FeatureCollection<GeoJSON.Polygon, T>,
  pixelDims: { width: number; height: number },
  labels?: {
    latLng: GeoJSON.Position;
    label: string;
    displayColor: string;
  }[]
) {
  const feats = (
    Array.isArray(inputFeats) ? inputFeats : inputFeats.features
  ).map((f) => cleanPolygon(withBbox(f)));
  if (!feats.length) throw new Error('No features');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const bboxes = feats.map((f) => f.bbox!);
  let bbox = combineBboxes(bboxes);
  if (!bbox) throw new Error('No bbox');
  bbox = addBufferToBbox(bbox, 30);
  const { sw, ne } = getBboxCorners(bbox);
  const zoom = Math.floor(getZoomLevel(bbox, pixelDims));
  const scale = zoomToScale(zoom);
  const viewport = getViewportFromBbox(bbox, scale);

  // get tiles within the feature's bounding box
  const swTile = latLngToTileXy(sw, scale);
  const neTile = latLngToTileXy(ne, scale);
  const tiles: {
    latLng: GeoJSON.Position;
    tile: { x: number; y: number };
  }[] = [];
  for (let x = swTile.x; x <= neTile.x; x++) {
    for (let y = neTile.y; y <= swTile.y; y++) {
      const latLng = tileXyToLatLng({ x, y }, scale);
      tiles.push({ latLng, tile: { x, y } });
    }
  }

  function toPixelConverter(latLng: GeoJSON.Position) {
    const worldCoord = moveCoord(
      project(latLng),
      negateCoord(project([sw[0], ne[1]]))
    );
    const pixel = [
      Math.floor(worldCoord[0] * scale),
      Math.floor(worldCoord[1] * scale),
    ];
    return pixel;
  }

  // convert tiles to pixel coordinates
  const convertedTiles = tiles.map(({ latLng, tile }) => {
    const pixel = toPixelConverter(latLng);
    return {
      latLng,
      tile,
      pixel,
    };
  });

  // fetch the tile images
  const tileImagesUnloaded = await Promise.all(
    convertedTiles.map(async ({ latLng, tile, pixel }) => {
      const blob = await getAerometrexTileImage(tile, zoom);
      // const url = URL.createObjectURL(blob);
      return {
        latLng,
        tile,
        pixel,
        // url,
        blob,
      };
    })
  );
  // load in parallel
  const tileImages = await Promise.all(
    tileImagesUnloaded.map(async ({ blob, ...rest }) => {
      const bitmap = await createImageBitmap(blob);
      return { bitmap, ...rest };
    })
  );

  // convert feat to draw paths
  const drawPaths = feats.flatMap((f): DrawPath[] => {
    if (f.geometry.type === 'MultiPolygon') {
      return f.geometry.coordinates.map((poly) => ({
        coordinates: poly.map((ring) => ring.map(toPixelConverter)),
        fillColor: f.properties.fillColor ?? DEFAULT_FILL_COLOR,
        strokeColor: f.properties.strokeColor ?? DEFAULT_STROKE_COLOR,
        fillOpacity: f.properties.fillOpacity ?? DEFAULT_FILL_OPACITY,
        strokeOpacity: f.properties.strokeOpacity ?? DEFAULT_STROKE_OPACITY,
        strokeWidth: f.properties.strokeWidth ?? DEFAULT_STROKE_WIDTH,
      }));
    } else {
      return [
        {
          coordinates: f.geometry.coordinates.map((ring) =>
            ring.map(toPixelConverter)
          ),
          fillColor: f.properties.fillColor ?? DEFAULT_FILL_COLOR,
          strokeColor: f.properties.strokeColor ?? DEFAULT_STROKE_COLOR,
          fillOpacity: f.properties.fillOpacity ?? DEFAULT_FILL_OPACITY,
          strokeOpacity: f.properties.strokeOpacity ?? DEFAULT_STROKE_OPACITY,
          strokeWidth: f.properties.strokeWidth ?? DEFAULT_STROKE_WIDTH,
        },
      ];
    }
  });

  const drawLabels = labels?.map((label) => {
    const pixel = toPixelConverter(label.latLng);
    return { ...label, pixel };
  });

  return {
    drawPaths,
    tileImages,
    viewport,
    drawLabels,
  };
}
export type GeoJson2SvgSetup = Awaited<ReturnType<typeof geoJson2SvgSetup>>;

const waitImageLoad = (img: HTMLImageElement): Promise<HTMLImageElement> =>
  new Promise((ok, reject) => {
    img.onload = () => ok(img);
    img.onerror = (err) => reject(err);
  });

/**
 * Draw paths script. Web ONLY (NOT worker friendly).
 */
export async function geoJson2SvgDrawPaths(
  // ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  svg: Svg,
  { drawPaths }: GeoJson2SvgSetup
) {
  for (const {
    coordinates,
    fillColor,
    fillOpacity,
    strokeColor,
    strokeOpacity,
    strokeWidth,
  } of drawPaths) {
    const group = svg.group();
    group
      .path(coordinates.map(xyToPathString).join(' '))
      .fill({ color: fillColor, opacity: fillOpacity })
      .attr('stroke-width', strokeWidth)
      .stroke({ color: strokeColor, opacity: strokeOpacity })
      .attr('fill-rule', 'evenodd');
  }
}

export async function geoJson2SvgDrawLabels(
  svg: Svg,
  { drawLabels }: GeoJson2SvgSetup
) {
  if (!drawLabels) return;
  for (const label of drawLabels) {
    const _createText = (g: G) => {
      const text = g
        .text(label.label)
        .font({
          family: 'monospace',
          size: 10,
          weight: 'normal',
          verticalAlign: 'middle',
        })
        .fill('#ffffff')
        .attr('alignment-baseline', 'top');
      return text;
    };
    const g = svg.group();
    _createText(g);
    const [w, h] = [Number(g.width()), Number(g.height())];
    const [px, py] = LABEL_STYLES.padding;
    const [rw, rh] = [w + 2 * px, h + 2 * py];
    g.rect(rw, rh) // top-left is 0,0 move
      .fill(label.displayColor)
      // .move(label.x - w*0.5, label.y);
      .move(label.pixel[0] - rw * 0.5, label.pixel[1] - rh * 0.5);
    const text = _createText(svg.group());
    text.move(label.pixel[0] - w * 0.5, label.pixel[1] - h * 0.5);
  }
}

/**
 * Final draw script. NOT worker friendly.
 */
export async function geoJson2SvgDraw(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  svg: string,
  { viewport, tileImages }: GeoJson2SvgSetup
) {
  for (const { bitmap, pixel } of tileImages) {
    ctx.drawImage(bitmap, pixel[0], pixel[1], TILE_SIZE, TILE_SIZE);
  }

  const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.src = svgUrl;
  await waitImageLoad(img);
  ctx.drawImage(img, 0, 0, viewport.width, viewport.height);
}

/**
 * Needed as canvas.toDataURL() not available with OffscreenCanvas.
 */
export const blobToDataUrl = async (data: Blob) =>
  new Promise<string>((ok, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        ok(reader.result);
      } else {
        reject(new Error(`Unexpected data type: ${typeof reader.result}`));
      }
    });
    reader.addEventListener('error', reject);
    reader.readAsDataURL(data);
  });

export function toGeoJson2SvgPolygonFeature<
  T extends GeoJson2SvgPolygonFeatureProperties,
>(
  geom: GeoJSON.Polygon | GeoJSON.MultiPolygon,
  properties: T
): GeoJson2SvgPolygonFeature<T> {
  return {
    type: 'Feature',
    geometry: geom,
    properties,
  };
}
